import React, { useState, useEffect } from "react";

const ConditionRenderer = ({ node, depth = 0 }) => {
  if (!node) return null;

  const flattenRules = (n, logic = "") => {
    let results = [];
    if (n.field) {
      results.push({ ...n, logic });
    } else if (n.rules) {
      n.rules.forEach((r, i) => {
        results = [...results, ...flattenRules(r, i === 0 ? n.type : "")];
      });
    }
    return results;
  };

  const rules = flattenRules(node);

  return (
    <div className="overflow-hidden border border-gray-200 rounded-lg">
      <table className="w-full text-left text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50 text-gray-500 font-bold border-b border-gray-200">
            <th className="px-3 py-2 border-r border-gray-100 w-16">Logic</th>
            <th className="px-3 py-2 border-r border-gray-100">Field</th>
            <th className="px-3 py-2 border-r border-gray-100">Operator</th>
            <th className="px-3 py-2">Value</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rules.map((rule, idx) => (
            <tr key={idx} className="hover:bg-gray-50 transition-colors">
              <td className="px-3 py-2 font-black text-indigo-500 bg-indigo-50/30 text-center uppercase tracking-tighter">
                {rule.logic || ""}
              </td>
              <td className="px-3 py-2 font-medium text-gray-700">{rule.field}</td>
              <td className="px-3 py-2 text-gray-400 font-mono uppercase text-[10px]">{rule.operator}</td>
              <td className="px-3 py-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${typeof rule.value === 'boolean' ? (rule.value ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700') : 'bg-gray-100 text-gray-700'}`}>
                  {String(rule.value)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RuleModal = ({ isOpen, onClose, condition }) => {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h3 className="text-lg font-semibold text-gray-800">Rule Logic</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-8 overflow-y-auto max-h-[70vh] bg-gray-50/30">
          {condition && Object.keys(condition).length > 0 ? (
            <div className="max-w-lg mx-auto">
              <ConditionRenderer node={condition} />
            </div>
          ) : (
            <div className="flex flex-col items-center py-10 text-gray-400">
              <svg className="w-12 h-12 mb-2 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm font-medium italic">No logic rules found for this banner.</p>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end bg-gray-50/50 gap-3">
          <div className="text-[10px] text-gray-400 self-center mr-auto font-mono">Press ESC to close</div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold rounded-xl shadow-lg shadow-indigo-100 transition-all active:scale-95"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};


const SampleTable = ({
  sampleList,
  fetchSampleList,
  pagination,
  setPagination
}) => {
  const [selectedRule, setSelectedRule] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (condition) => {
    setSelectedRule(condition);
    setIsModalOpen(true);
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  const FIELDS_TO_SHOW = [
    "id", "pii", "template", "is_active", "is_check_time", "is_check_datetime",
    "is_check_cond", "startdateTime", "enddateTime", "time", "status",
    "createdBy", "created_at", "updated_at"
  ];

  return (
    <div className="px-4 pb-10">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Table Header with Page Size */}
        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap justify-between items-center bg-gray-50/50 gap-4">
          <h2 className="text-base font-semibold text-gray-800 whitespace-nowrap">Sample List</h2>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Show</span>
              <select
                value={pagination.pageSize}
                onChange={(e) => fetchSampleList(1, Number(e.target.value))}
                className="text-xs border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-100 bg-white font-medium"
              >
                {[10, 20, 50, 100].map(sz => <option key={sz} value={sz}>{sz} rows</option>)}
              </select>
            </div>
            <button
              onClick={() => fetchSampleList(pagination.page, pagination.pageSize)}
              className="p-1.5 text-gray-400 hover:text-indigo-600 transition-colors hover:bg-indigo-50 rounded-lg"
              title="Refresh"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                {FIELDS_TO_SHOW.map(field => (
                  <th key={field} className="px-4 py-2 border-r border-gray-100 whitespace-nowrap uppercase text-[10px] tracking-wider">
                    {field}
                  </th>
                ))}
                <th className="px-4 py-2 text-right sticky right-0 bg-gray-50 border-l border-gray-200 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sampleList?.length === 0 ? (
                <tr>
                  <td colSpan={FIELDS_TO_SHOW.length + 1} className="px-4 py-10 text-center text-gray-400 italic">
                    No samples found.
                  </td>
                </tr>
              ) : (
                sampleList?.map((sample) => (
                  <tr key={sample.id} className="hover:bg-gray-50/50 transition-colors group">
                    {FIELDS_TO_SHOW.map(field => (
                      <td key={field} className="px-4 py-2 text-[11px] text-gray-700 whitespace-nowrap border-r border-gray-50">
                        {/* ... value rendering ... */}
                        {typeof sample[field] === 'boolean' ? (
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${sample[field] ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {String(sample[field])}
                          </span>
                        ) : (
                          sample[field] || '-'
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right sticky right-0 bg-white group-hover:bg-gray-50 transition-colors border-l border-gray-200 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => openModal(sample.condition)}
                          className="text-indigo-600 hover:text-indigo-800 font-medium text-[11px] underline underline-offset-2 whitespace-nowrap"
                        >
                          View Rule
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="text-[11px] text-gray-500 font-medium">
            Showing <span className="text-gray-800">{(pagination.page - 1) * pagination.pageSize + 1}</span> to <span className="text-gray-800">{Math.min(pagination.page * pagination.pageSize, pagination.total)}</span> of <span className="text-gray-800">{pagination.total}</span> entries
          </div>
          <div className="flex items-center gap-1.5">
            <button
              disabled={pagination.page <= 1}
              onClick={() => fetchSampleList(pagination.page - 1, pagination.pageSize)}
              className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex items-center gap-1">
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                // Show first, last, and pages around current
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => fetchSampleList(pageNum, pagination.pageSize)}
                      className={`w-7 h-7 text-[11px] font-bold rounded-lg transition-all ${pagination.page === pageNum
                          ? 'bg-indigo-500 text-white shadow-md'
                          : 'text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                }
                // Show ellipses
                if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                  return <span key={pageNum} className="text-gray-400 text-[10px]">...</span>;
                }
                return null;
              })}
            </div>

            <button
              disabled={pagination.page >= totalPages}
              onClick={() => fetchSampleList(pagination.page + 1, pagination.pageSize)}
              className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <RuleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        condition={selectedRule}
      />
    </div>
  );
};



export default SampleTable;
